import React from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Table from "../../../../core/common/dataTable";
import { useBulkBookRequest } from "../hooks/useBulkBookRequest";

const BulkBookRequest = () => {
  const routes = all_routes;

  const {
    selectedSession,
    setSelectedSession,
    selectedCourse,
    setSelectedCourse,
    selectedStage,
    setSelectedStage,
    selectedStudentKeys,
    pageNumber,
    recordsPerPage,
    isRequestingBulk,
    sessions,
    isLoadingSessions,
    courses,
    isLoadingCourses,
    isLoadingStudents,
    paginationData,
    transformedStudents,
    getStageOptions,
    handleSelectionChange,
    handleSearchChange,
    handlePageChange,
    handleRequestAll,
    handleRequestIndividual,
    getCheckboxProps,
    areAllFiltersSelected,
  } = useBulkBookRequest();

  const columns = [
    {
      title: "Student ID",
      dataIndex: "studentId",
      key: "studentId",
      render: (text: string) => (
        <Link to="#" className="link-primary">
          {text}
        </Link>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "name",
      key: "name",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <p className="text-dark mb-0">{text}</p>
        </div>
      ),
    },
    {
      title: "Course",
      key: "courseName",
      dataIndex: "courseName",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <p className="text-dark mb-0">{text}</p>
        </div>
      ),
    },
    {
      title: "Level",
      key: "stageName",
      dataIndex: "stageName",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <p className="text-dark mb-0">{text}</p>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: string, record: any) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <button
                  className="dropdown-item rounded-1"
                  onClick={() => handleRequestIndividual(record)}
                  disabled={record.hasActiveRequest}
                >
                  <i className="ti ti-book me-2" />
                  {record.hasActiveRequest ? "Requested" : "Request Book"}
                </button>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Bulk Book Request</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.bookRequestList}>Book Requests</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Bulk Book Request
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Bulk Book Request</h4>
            </div>
            <div className="card-body">
              {/* Filters Section */}
              <div className="row gx-2 mb-4">
                <div className="col-md-4 mb-2">
                  <label className="form-label">Session</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedSession}
                    onChange={(e) => setSelectedSession(e.target.value)}
                    disabled={isLoadingSessions}
                  >
                    <option value="">Select Session</option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id.toString()}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 mb-2">
                  <label className="form-label">Course</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedCourse}
                    onChange={(e) => {
                      setSelectedCourse(e.target.value);
                      setSelectedStage(""); // Reset stage on course change
                    }}
                    disabled={isLoadingCourses || !selectedSession}
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id.toString()}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 mb-2">
                  <label className="form-label">Level</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedStage}
                    onChange={(e) => setSelectedStage(e.target.value)}
                    disabled={!selectedCourse}
                  >
                    <option value="">Select Level</option>
                    {getStageOptions().map((stage) => (
                      <option key={stage.id} value={stage.id.toString()}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Highlighted Bulk Actions Bar - Visible only when list is visible */}
              {areAllFiltersSelected() && (
                <div className="mb-4">
                  <div className="bulk-actions-highlight bg-primary text-white rounded shadow-md p-3 d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-white text-primary rounded-circle d-flex align-items-center justify-content-center me-3 fw-bold fs-3 shadow-sm" style={{ width: "48px", height: "48px", minWidth: "48px" }}>
                        {selectedStudentKeys.length}
                      </div>
                      <div>
                        <h6 className="text-white mb-0 fw-bold">Students Selected</h6>
                        <p className="text-white-50 small mb-0">Select multiple students to request books for at once</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-grow-1 justify-content-md-end">
                      <button
                        className="btn btn-warning rounded px-4 fw-bold h-100 d-flex align-items-center"
                        onClick={handleRequestAll}
                        disabled={selectedStudentKeys.length === 0 || isRequestingBulk}
                        style={{ height: "42px" }}
                      >
                        {isRequestingBulk ? (
                          <>
                            <i className="ti ti-loader animate-spin me-2" />
                            Requesting...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-book me-2" />
                            Request Selected
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Table Section */}
              {areAllFiltersSelected() ? (
                isLoadingStudents ? (
                  <div className="text-center">Loading student data...</div>
                ) : (
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={transformedStudents}
                      rowKey="id"
                      Selection={true}
                      onSelectionChange={handleSelectionChange}
                      selectedKeys={selectedStudentKeys}
                      onSearchChange={handleSearchChange}
                      getCheckboxProps={getCheckboxProps}
                      pagination={{
                        current: pageNumber,
                        pageSize: recordsPerPage,
                        total: paginationData?.totalItems || 0,
                        onChange: handlePageChange,
                        pageSizeOptions: ["All", 10, 20, 30],
                      }}
                    />
                  </div>
                )
              ) : (
                <div className="text-center py-5">
                  <i className="ti ti-filter fs-1 text-muted mb-3"></i>
                  <h4>Please select all filters to view the list</h4>
                  <p className="text-warning">
                    Select Session, Course, and Level to see the student list
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkBookRequest;
